import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { UseFormReturn } from 'react-hook-form'
import type {
  CreateSageHrServiceConnectionModel,
  ServiceConnectionModel,
  ServiceConnectionType,
  UpdateSageHrServiceConnectionModel,
} from '@/api/model'

/**
 * Stands in for a stored password the user has not retyped. Same literal as
 * `EditInsuranceServiceModule` — the backend keeps the stored secret when the field
 * comes back empty or unchanged.
 */
export const PASSWORD_PLACEHOLDER = '*****'

/** A type's hardcoded icon: a lucide glyph, or a brand asset once one is supplied. */
export type ServiceConnectionIcon =
  | { kind: 'lucide'; icon: LucideIcon }
  | { kind: 'image'; src: string }

/**
 * Form state of the connection dialog: the shared `name` plus every type's own fields.
 * Flat rather than a per-type union so the dialog can hold a single `useForm` and
 * `isDirty` stays meaningful. A new connection type appends its fields here and adds
 * its definition — nothing else changes.
 */
export interface ServiceConnectionFormValues {
  name: string

  // SageHr
  apiBaseUrl: string
  username: string
  databaseKey: string
  /** Kept as a string like every other field; converted to a number in the payloads. */
  mandateId: string
  password: string
}

/** The single source for "no values yet" — seeds the dialog's form before a type is known. */
export const emptyServiceConnectionFormValues: ServiceConnectionFormValues = {
  name: '',
  apiBaseUrl: '',
  username: '',
  databaseKey: '',
  mandateId: '',
  password: '',
}

export interface ServiceConnectionFieldsProps {
  form: UseFormReturn<ServiceConnectionFormValues>
  isCreate: boolean
}

/**
 * Request bodies the dialog can send. Orval collapses the polymorphic schema to the only
 * concrete subtype while `ServiceConnectionType` has a single value; a second type turns
 * the generated parameter into a union, and these aliases grow with `| CreateXyz…`.
 */
export type CreateServiceConnectionPayload = CreateSageHrServiceConnectionModel
export type UpdateServiceConnectionPayload = UpdateSageHrServiceConnectionModel

/**
 * Everything the UI knows about one connection type. Adding a type = appending one
 * definition to `registry/index.ts`; table, dialog and create menu stay untouched.
 */
export interface ServiceConnectionTypeDefinition {
  type: ServiceConnectionType
  icon: ServiceConnectionIcon
  /** Key inside the `service-connections` namespace, e.g. `types.SageHr.label`. */
  labelKey: string
  /** Complete defaults for every field — the empty create form and the close-reset. */
  defaultValues: ServiceConnectionFormValues
  /** The fields only this type has; the shared `name` is rendered by the dialog. */
  Fields: (props: ServiceConnectionFieldsProps) => ReactNode
  fromModel: (model: ServiceConnectionModel) => ServiceConnectionFormValues
  toCreatePayload: (values: ServiceConnectionFormValues) => CreateServiceConnectionPayload
  toUpdatePayload: (values: ServiceConnectionFormValues) => UpdateServiceConnectionPayload
}
